import { useEffect, useState } from 'react'
import { RegistrationAPI } from '../services/registrationApi'
import './RegisterPage.css'
import { scopedSessionStorage } from '../lib/storage'
import { SEED_TOKENS_KEY } from '@it-sce/ui'

// Public self-registration page (TQ #6854/#6855, ported from the standalone
// registration/ app). One page, local step-state:
//   ?token=<code> in URL (magic-link from the mail) → start on 'verify'
//   otherwise                                        → start on 'email'
// Flow: email → verify → done. No Keycloak auth — the portal-api endpoints
// /public/registration/{request,complete} are unauthenticated.

type Step = 'email' | 'verify' | 'done'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const PORTAL_URL = 'https://with.it-sce.de'
const loginUrl = (email: string) =>
  `${PORTAL_URL}/?login_hint=${encodeURIComponent(email)}`

const AUTO_REDIRECT_SECONDS = 10

// Personal-tier service suggestions on the welcome screen. Every tile leads to
// the portal login where the concrete service is chosen.
const SERVICES: { icon: string; title: string; desc: string }[] = [
  { icon: '✉️', title: 'E-Mail-Postfach', desc: 'Eigene @scemail.de-Adresse, werbefrei und in Deutschland gehostet.' },
  { icon: '☁️', title: 'Dateispeicher', desc: 'Sichere Cloud für Dokumente, Fotos und Backups.' },
  { icon: '🤖', title: 'KI-Assistent Aria', desc: 'Dein persönlicher Assistent, der deine Dienste bedient.' },
  { icon: '🔐', title: 'Passwort-Manager', desc: 'Alle Zugangsdaten verschlüsselt an einem Ort.' },
]

function readTokenFromUrl(): string {
  if (typeof window === 'undefined') return ''
  return (new URLSearchParams(window.location.search).get('token') || '')
    .replace(/\D/g, '')
    .slice(0, 6)
}

// IT-SCE Workspace badge — dark navy mark (source: customer-portal favicon.svg).
function Logo() {
  return (
    <div className="register-logo">
      <svg width="72" height="72" viewBox="0 0 64 64" role="img" aria-label="IT-SCE Workspace">
        <rect width="64" height="64" rx="12" fill="#1a2235" />
        <text x="32" y="26" fontFamily="Arial, Helvetica, sans-serif" fontSize="13" fontWeight="700" fill="#FE4F00" textAnchor="middle" letterSpacing="0.5">IT-SCE</text>
        <rect x="14" y="30" width="36" height="2" rx="1" fill="#FE4F00" opacity="0.6" />
        <text x="32" y="48" fontFamily="Arial, Helvetica, sans-serif" fontSize="11" fontWeight="600" fill="#FFFFFF" textAnchor="middle" opacity="0.85">Workspace</text>
      </svg>
    </div>
  )
}

export default function RegisterPage() {
  const urlToken = readTokenFromUrl()
  const [step, setStep] = useState<Step>(urlToken ? 'verify' : 'email')
  const [email, setEmail] = useState('')
  const [doneEmail, setDoneEmail] = useState('')
  const [doneFirstName, setDoneFirstName] = useState('')
  // TQ #7057: true when auto-login tokens were seeded → WelcomeStep redirects to '/'.
  const [doneSeeded, setDoneSeeded] = useState(false)

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-header">
          <Logo />
        </div>
        <div className="register-card">
          {step === 'email' && (
            <EmailStep
              initialEmail={email}
              onHaveCode={(e) => {
                setEmail(e)
                setStep('verify')
              }}
            />
          )}
          {step === 'verify' && (
            <VerifyStep
              initialCode={urlToken}
              knownEmail={email || undefined}
              onBack={() => setStep('email')}
              onSuccess={(e, fn, seeded) => {
                setDoneEmail(e)
                setDoneFirstName(fn)
                setDoneSeeded(seeded)
                setStep('done')
              }}
            />
          )}
          {step === 'done' && (
            <WelcomeStep email={doneEmail} firstName={doneFirstName} seeded={doneSeeded} />
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 1: email entry → POST /public/registration/request (+ honeypot).
// ---------------------------------------------------------------------------
interface EmailStepProps {
  initialEmail?: string
  onHaveCode: (email: string) => void
}

function EmailStep({ initialEmail = '', onHaveCode }: EmailStepProps) {
  const [email, setEmail] = useState(initialEmail)
  const [website, setWebsite] = useState('') // honeypot — real users never fill this
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  const addr = () => email.trim().toLowerCase()

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!EMAIL_RE.test(addr())) {
      setError('Bitte eine gültige E-Mail-Adresse eingeben')
      return
    }
    setSubmitting(true)
    try {
      await RegistrationAPI.requestEmail({ email: addr(), website })
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mail konnte nicht gesendet werden')
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <div className="register-centered">
        <div className="register-icon-circle">✉️</div>
        <h1>Fast geschafft!</h1>
        <p className="register-subtitle">Wir haben einen Bestätigungscode an</p>
        <p className="register-email-echo">{addr()}</p>
        <p className="register-subtitle">
          geschickt. Klicke den Link in der Mail — oder gib den Code hier ein.
        </p>
        <button type="button" className="btn-register-primary" onClick={() => onHaveCode(addr())}>
          Code eingeben
        </button>
        <p className="register-footnote">Keine Mail erhalten? Prüfe den Spam-Ordner.</p>
      </div>
    )
  }

  return (
    <form onSubmit={submit} noValidate>
      <h1>Konto erstellen</h1>
      <p className="register-subtitle">
        Mit deiner privaten E-Mail — kostenlos, jederzeit kündbar.
      </p>

      <div className="register-field">
        <label htmlFor="reg-email">E-Mail-Adresse</label>
        <input
          id="reg-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          disabled={submitting}
          className="register-input"
        />
      </div>

      {/* Honeypot — off-screen, hidden from AT; a filled value = bot. */}
      <div aria-hidden className="register-honeypot">
        <label>
          Website (bitte leer lassen)
          <input
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>

      {error && <div className="register-error">{error}</div>}

      <button type="submit" className="btn-register-primary" disabled={submitting}>
        {submitting ? 'Wird gesendet…' : 'E-Mail bestätigen'}
      </button>

      <p className="register-footnote">
        Schon registriert? <a href={PORTAL_URL}>Anmelden</a>
      </p>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Step 2: 6-digit code + profile + AGB → POST /public/registration/complete.
// ---------------------------------------------------------------------------
interface VerifyStepProps {
  initialCode?: string
  /** Pre-filled + read-only when we already know it (same-session step 1). */
  knownEmail?: string
  /** seeded=true when auto-login tokens were stored (TQ #7057) → redirect to '/'. */
  onSuccess: (email: string, firstName: string, seeded: boolean) => void
  onBack: () => void
}

function VerifyStep({ initialCode = '', knownEmail, onSuccess, onBack }: VerifyStepProps) {
  const [code, setCode] = useState(initialCode.replace(/\D/g, '').slice(0, 6))
  const [email, setEmail] = useState(knownEmail ?? '')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [agb, setAgb] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Bitte den 6-stelligen Code aus der Mail eingeben')
      return
    }
    if (firstName.trim().length < 2 || lastName.trim().length < 2) {
      setError('Bitte Vor- und Nachnamen eingeben')
      return
    }
    if (password.length < 8 || !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Passwort: mindestens 8 Zeichen, Groß- und Kleinbuchstaben sowie eine Zahl')
      return
    }
    if (password !== confirm) {
      setError('Passwörter stimmen nicht überein')
      return
    }
    if (!agb) {
      setError('Bitte akzeptiere die AGB und Datenschutzerklärung')
      return
    }
    setSubmitting(true)
    try {
      const res = await RegistrationAPI.completeRegistration({
        token: code.trim(),
        email: email.trim().toLowerCase() || undefined,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        agb,
      })

      // TQ #7057 (Option A — auto-login after registration): if the backend
      // minted a session via Direct Grant, seed the tokens one-shot into
      // sessionStorage. KeycloakAuthContext adopts them on next init (at '/')
      // so the user lands logged-in without a redirect. If no tokens are
      // present, we leave the seed unset and fall back to the login redirect.
      let seeded = false
      if (res.access_token) {
        try {
          scopedSessionStorage.setItem(
            SEED_TOKENS_KEY,
            JSON.stringify({
              access_token: res.access_token,
              refresh_token: res.refresh_token,
              id_token: res.id_token,
              expires_in: res.expires_in,
            })
          )
          seeded = true
        } catch {
          // sessionStorage unavailable (private mode quota etc.) → fall back.
          seeded = false
        }
      }
      onSuccess(res.email || email.trim().toLowerCase(), firstName.trim(), seeded)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registrierung fehlgeschlagen')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <h1>Registrierung abschließen</h1>
      <p className="register-subtitle">
        Gib den Code aus der Mail ein und vervollständige dein Profil.
      </p>

      <div className="register-field">
        <label htmlFor="reg-code">Bestätigungscode</label>
        <input
          id="reg-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          disabled={submitting}
          placeholder="000000"
          className="register-input code-input"
        />
      </div>

      <div className="register-field">
        <label htmlFor="reg-vemail">E-Mail-Adresse</label>
        <input
          id="reg-vemail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting || !!knownEmail}
          readOnly={!!knownEmail}
          placeholder="name@example.com"
          autoComplete="email"
          className="register-input"
        />
      </div>

      <div className="register-field register-row">
        <div>
          <label htmlFor="reg-fn">Vorname</label>
          <input
            id="reg-fn"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={submitting}
            autoComplete="given-name"
            className="register-input"
          />
        </div>
        <div>
          <label htmlFor="reg-ln">Nachname</label>
          <input
            id="reg-ln"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={submitting}
            autoComplete="family-name"
            className="register-input"
          />
        </div>
      </div>

      <div className="register-field">
        <label htmlFor="reg-pw">Passwort</label>
        <div className="register-pw-wrap">
          <input
            id="reg-pw"
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            autoComplete="new-password"
            className="register-input"
          />
          <button
            type="button"
            tabIndex={-1}
            className="register-pw-toggle"
            onClick={() => setShowPw(!showPw)}
          >
            {showPw ? 'verbergen' : 'zeigen'}
          </button>
        </div>
        <p className="register-hint">
          Mindestens 8 Zeichen, Groß- und Kleinbuchstaben, Zahlen
        </p>
      </div>

      <div className="register-field">
        <label htmlFor="reg-pwc">Passwort bestätigen</label>
        <input
          id="reg-pwc"
          type={showPw ? 'text' : 'password'}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={submitting}
          autoComplete="new-password"
          className="register-input"
        />
      </div>

      <label className="register-agb">
        <input
          type="checkbox"
          checked={agb}
          onChange={(e) => setAgb(e.target.checked)}
          disabled={submitting}
        />
        <span>
          Ich akzeptiere die{' '}
          <a href="https://www.it-sce.de/agb" target="_blank" rel="noreferrer">AGB</a>{' '}
          und die{' '}
          <a href="https://www.it-sce.de/datenschutz" target="_blank" rel="noreferrer">
            Datenschutzerklärung
          </a>
          .
        </span>
      </label>

      {error && <div className="register-error">{error}</div>}

      <button type="submit" className="btn-register-primary" disabled={submitting}>
        {submitting ? 'Wird erstellt…' : 'Konto erstellen'}
      </button>
      <button type="button" className="btn-register-secondary" onClick={onBack} disabled={submitting}>
        Zurück
      </button>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Welcome: greeting + service tiles + gentle (cancelable) auto-login redirect.
// ---------------------------------------------------------------------------
interface WelcomeStepProps {
  email: string
  firstName?: string
  /** TQ #7057: auto-login tokens were seeded → go to '/' (adopt session) not the KC login. */
  seeded?: boolean
}

function WelcomeStep({ email, firstName, seeded = false }: WelcomeStepProps) {
  const [seconds, setSeconds] = useState(AUTO_REDIRECT_SECONDS)
  const [autoLogin, setAutoLogin] = useState(true)

  // TQ #7057: when tokens were seeded, send the user to the app root where
  // KeycloakAuthContext adopts the seeded session (no KC redirect). Without
  // tokens, keep the original login-redirect fallback.
  const target = seeded ? '/' : loginUrl(email)

  useEffect(() => {
    if (!autoLogin) return
    if (seconds <= 0) {
      window.location.assign(target)
      return
    }
    const t = window.setTimeout(() => setSeconds((s) => s - 1), 1000)
    return () => window.clearTimeout(t)
  }, [seconds, autoLogin, target])

  const greeting = firstName ? `Willkommen bei IT-SCE, ${firstName}!` : 'Willkommen bei IT-SCE!'

  return (
    <div className="register-centered">
      <div className="register-icon-circle success">✅</div>
      <h1>{greeting}</h1>
      <p className="register-subtitle">Dein Konto wurde erstellt für</p>
      <p className="register-email-echo">{email}</p>

      <div className="register-tiles">
        {SERVICES.map((s) => (
          <a key={s.title} href={target} className="register-tile">
            <span className="register-tile-icon">{s.icon}</span>
            <span>
              <span className="register-tile-title">{s.title}</span>
              <span className="register-tile-desc">{s.desc}</span>
            </span>
          </a>
        ))}
      </div>

      <a href={target} className="btn-register-primary" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
        Jetzt anmelden &amp; loslegen
      </a>

      {autoLogin ? (
        <p className="register-footnote">
          Automatische Anmeldung in {seconds}s …{' '}
          <button
            type="button"
            onClick={() => setAutoLogin(false)}
            style={{ background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', color: 'inherit', font: 'inherit' }}
          >
            hier bleiben
          </button>
        </p>
      ) : (
        <p className="register-footnote">
          Wähle oben einen Dienst oder melde dich an, wenn du bereit bist.
        </p>
      )}
    </div>
  )
}
